import { isBlockColor, type BlockColor } from "@/lib/ile/palette";

/**
 * Lesson tables are stored as markdown inside rich_text, optionally wrapped in
 * `:::table-colors <colour per column…> [rows]` (see components/Markdown.tsx). The builder's
 * table editor works on this grid model and converts both ways, so authors never type the
 * markdown and every existing table can be opened and edited.
 */
export type TableModel = {
  headers: string[];
  rows: string[][];
  /** One per column; null leaves the column uncoloured. */
  colors: (BlockColor | null)[];
  /** Stripe alternate body rows. */
  stripes: boolean;
  /** Per-column alignment from the separator row (`:---:` etc.), kept so an edit doesn't drop it. */
  align?: Align[];
};

export type Align = "left" | "center" | "right" | null;

/** Columns the colour wrapper can colour (data-c1 … data-c6). */
export const MAX_COLORED_COLUMNS = 6;

export function emptyTable(columns = 3, rows = 2): TableModel {
  return {
    headers: Array.from({ length: columns }, (_, i) => `Column ${i + 1}`),
    rows: Array.from({ length: rows }, () => Array(columns).fill("")),
    colors: Array(columns).fill(null),
    stripes: false,
  };
}

const escapeCell = (cell: string) => cell.replace(/\r?\n/g, " ").replace(/\\?\|/g, "\\|").trim();

const line = (cells: string[]) => `| ${cells.map((c) => escapeCell(c) || " ").join(" | ")} |`;

export function tableToMarkdown(table: TableModel): string {
  const width = table.headers.length;
  const pad = (cells: string[]) => Array.from({ length: width }, (_, i) => cells[i] ?? "");
  const rule = (a: Align | undefined) => (a === "center" ? ":---:" : a === "right" ? "---:" : a === "left" ? ":---" : "---");
  const separator = `|${Array.from({ length: width }, (_, i) => rule(table.align?.[i])).join("|")}|`;
  const body = [line(table.headers), separator, ...table.rows.map((r) => line(pad(r)))].join("\n");

  const colors = table.colors.slice(0, MAX_COLORED_COLUMNS).map((c) => c ?? "-");
  while (colors.length && colors[colors.length - 1] === "-") colors.pop();
  const words = [...colors, ...(table.stripes ? ["rows"] : [])];
  return words.length ? `:::table-colors ${words.join(" ")}\n${body}\n:::` : body;
}

/** Cells of one markdown table row; `\|` is a literal pipe, not a cell break. */
function splitRow(row: string): string[] {
  let s = row.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  return s.split(/(?<!\\)\|/).map((c) => c.replace(/\\\|/g, "|").trim());
}

const isRow = (l: string) => l.trimStart().startsWith("|");
const isSeparator = (l: string) => /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(l);
const isOpener = (l: string) => /^\s*:::table-colors\b/.test(l);
const isCloser = (l: string) => /^\s*:::\s*$/.test(l);

/**
 * The table the cursor sits in (its rows, or its `:::table-colors` / closing `:::` line), as a
 * character range to replace and the grid it parses to. Null when the cursor isn't in a table.
 */
export function findTableAt(text: string, cursor: number): { start: number; end: number; table: TableModel } | null {
  const lines = text.split("\n");
  const starts: number[] = [];
  let offset = 0;
  for (const l of lines) {
    starts.push(offset);
    offset += l.length + 1;
  }
  let at = starts.findLastIndex((s) => s <= cursor);
  if (at < 0) return null;

  // Step from the wrapper lines onto the table itself.
  if (isOpener(lines[at]) && isRow(lines[at + 1] ?? "")) at += 1;
  else if (isCloser(lines[at]) && isRow(lines[at - 1] ?? "")) at -= 1;
  if (!isRow(lines[at])) return null;

  let first = at;
  while (first > 0 && isRow(lines[first - 1])) first -= 1;
  let last = at;
  while (last < lines.length - 1 && isRow(lines[last + 1])) last += 1;
  if (last - first < 1 || !isSeparator(lines[first + 1])) return null;

  const headers = splitRow(lines[first]);
  const width = headers.length;
  const rules = splitRow(lines[first + 1]);
  const align: Align[] = headers.map((_, i) => {
    const r = rules[i] ?? "";
    return r.startsWith(":") && r.endsWith(":") ? "center" : r.endsWith(":") ? "right" : r.startsWith(":") ? "left" : null;
  });
  const rows = lines.slice(first + 2, last + 1).map((l) => {
    const cells = splitRow(l);
    return Array.from({ length: width }, (_, i) => cells[i] ?? "");
  });

  let colors: (BlockColor | null)[] = Array(width).fill(null);
  let stripes = false;
  if (first > 0 && isOpener(lines[first - 1]) && isCloser(lines[last + 1] ?? "")) {
    const words = lines[first - 1].trim().replace(/^:::table-colors/, "").split(/[\s,]+/).filter(Boolean);
    stripes = words.includes("rows");
    const listed = words.filter((w) => w !== "rows");
    colors = colors.map((_, i) => (listed[i] && isBlockColor(listed[i]) ? listed[i] : null));
    first -= 1;
    last += 1;
  }

  const end = last === lines.length - 1 ? text.length : starts[last] + lines[last].length;
  const table: TableModel = { headers, rows, colors, stripes };
  if (align.some(Boolean)) table.align = align;
  return { start: starts[first], end, table };
}

/**
 * Put a new table at `cursor` on its own lines, with a blank line on each side (a table
 * straight after a paragraph line would be read as part of that paragraph).
 */
export function insertTable(text: string, cursor: number, markdown: string): { text: string; start: number; end: number } {
  const before = text.slice(0, cursor);
  const after = text.slice(cursor);
  const lead = before === "" || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
  const trail = after === "" ? "\n" : after.startsWith("\n\n") ? "" : after.startsWith("\n") ? "\n" : "\n\n";
  const start = before.length + lead.length;
  return { text: before + lead + markdown + trail + after, start, end: start + markdown.length };
}

/**
 * Where a new table may go. Inside table-like lines that don't parse as a table (a typo in the
 * separator row, say), inserting at the cursor would split them, so the new table goes after them.
 */
export function insertPoint(text: string, cursor: number): number {
  const lines = text.split("\n");
  let offset = 0;
  let at = -1;
  for (let i = 0; i < lines.length; i++) {
    if (offset + lines[i].length >= cursor) {
      at = i;
      break;
    }
    offset += lines[i].length + 1;
  }
  if (at < 0 || !(isRow(lines[at]) || isOpener(lines[at]))) return cursor;
  let last = at;
  while (last < lines.length - 1 && (isRow(lines[last + 1]) || isCloser(lines[last + 1]))) {
    last += 1;
    if (isCloser(lines[last])) break;
  }
  return lines.slice(0, last + 1).join("\n").length;
}
